import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";

import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Button,
  Row,
  Col,
  message,
  Divider,
  Space,
  Spin,
} from "antd";

import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";

import userService from "../services/userService";
import {
  destinationService
} from "../services/destinationService";

import { leadService } from "../services/leadService";
import { useAuth } from "../context/AuthContext";

const { Option } = Select;
const { TextArea } = Input;

const LeadFormModal = ({
  visible,
  onClose,
  onSuccess,
  editingLead = null,
  mode = "create",
}) => {

  const [form] = Form.useForm();

  const { user } = useAuth();

const navigate = useNavigate();

  const [loading, setLoading] =
    useState(false);

  const [users, setUsers] =
    useState([]);

  const [tags, setTags] =
    useState([]);

  const [
    destinationOptions,
    setDestinationOptions,
  ] = useState([]);

  const [
    destinationLoading,
    setDestinationLoading,
  ] = useState(false);

  /* =====================================================
     INITIAL LOAD
  ===================================================== */

useEffect(() => {

    if (!visible) return;

    const privilegedRoles = [
        "admin",
        "superadmin",
        "sales_manager",
        "team_lead"
    ];

    if (privilegedRoles.includes(user?.role)) {
        fetchUsers();
    } else {
        setUsers([]);
    }

    fetchTags();
    fetchDestinations();

    if (
        editingLead &&
        mode === "edit"
    ) {
        populateForm(editingLead);
    } else {
        form.resetFields();
    }

}, [
    visible,
    editingLead,
    mode,
    user
]);

  /* =====================================================
     USERS
  ===================================================== */

  const fetchUsers =
    async () => {

      try {

        const response =
          await userService.getUsers();

        if (response.data) {

          setUsers(

            response.data?.data ||

            response.data?.users ||

            []
          );
        }

      } catch (error) {

        console.error(
          "Error fetching users:",
          error
        );
      }
    };

  /* =====================================================
     TAGS
  ===================================================== */

  const fetchTags =
    async () => {

      try {

        setTags([

          "Hot Lead",

          "Corporate",

          "Honeymoon",

          "Family Trip",

          "Adventure",

          "Luxury",
        ]);

      } catch (error) {

        console.error(
          "Error fetching tags:",
          error
        );
      }
    };

  /* =====================================================
     DESTINATIONS
  ===================================================== */

const fetchDestinations =
  async () => {

    try {

      setDestinationLoading(
        true
      );

      const response =
        await destinationService.getAll();

      console.log(
        "DESTINATION RESPONSE:",
        response
      );

      /* =====================================
         HANDLE SERVICE RESPONSE
      ===================================== */

      const destinations =

        Array.isArray(
          response
        )

          ? response

          : Array.isArray(
              response?.data
            )

              ? response.data

              : [];

      console.log(
        "DESTINATIONS:",
        destinations
      );

      /* =====================================
         BUILD OPTIONS
      ===================================== */

      const options =
        destinations

          .filter(
            (d) =>
              d &&
              d._id &&
              d.name
          )

          .map((d) => ({

            value:
              d._id,

            label:
              d.name,
          }));

      console.log(
        "OPTIONS:",
        options
      );

      setDestinationOptions(
        options
      );

    } catch (error) {

      console.error(
        "Error fetching destinations:",
        error
      );

      setDestinationOptions([]);

    } finally {

      setDestinationLoading(
        false
      );
    }
  };


  const populateForm =
    (lead) => {

      const selectedDests = Array.isArray(lead.destinations) && lead.destinations.length
        ? lead.destinations.map(d => d?._id || d)
        : (lead.destination?._id || lead.destination ? [lead.destination?._id || lead.destination] : []);

      const formData = {

        customerName:
          lead.customerName,

        email:
          lead.email,

        phone:
          lead.phone,

        alternatePhone:
          lead.alternatePhone,

        destinations:
          selectedDests,

        travelDate:

          lead.travelDate

            ? dayjs(
                lead.travelDate
              )

            : null,

        returnDate:

          lead.returnDate

            ? dayjs(
                lead.returnDate
              )

            : null,

        adults:
          lead.pax?.adults || 1,

        children:
          lead.pax?.children || 0,

        infants:
          lead.pax?.infants || 0,

        budgetMin:
          lead.budget?.min,

        budgetMax:
          lead.budget?.max,

        priority:
          lead.priority,

        source:
          lead.source,

        assignedTo:

          lead.assignedTo?._id ||

          lead.assignedTo,

        specialRequests:
          lead.specialRequests,

        notes:
          lead.notes,

        tags:
          lead.tags || [],
      };

      form.setFieldsValue(
        formData
      );
    };

  /* =====================================================
     SUBMIT
  ===================================================== */

  const handleSubmit =
    async (values) => {

      setLoading(true);

      try {

        const selectedDests = Array.isArray(values.destinations)
          ? values.destinations
          : (values.destination ? [values.destination] : []);

        const selectedDestObjects = destinationOptions.filter((d) =>
          selectedDests.includes(d.value)
        );

        const destSnapshotString = selectedDestObjects.map((d) => d.label).join(", ");

        const leadData = {

          customerName:
            values.customerName,

          email:
            values.email,

          phone:
            values.phone,

          alternatePhone:
            values.alternatePhone,

          /* =====================================
             DESTINATION NORMALIZATION
          ===================================== */

          destinations:
            selectedDests,

          destination:
            selectedDests[0] || values.destination,

          destinationSnapshot:
            destSnapshotString || "",

          travelDate:
            values.travelDate,

          returnDate:
            values.returnDate,

          duration:
            Number(values.duration || 0),

          pax: {

            adults:
              values.adults || 1,

            children:
              values.children || 0,

            infants:
              values.infants || 0,
          },

          budget: {

            min:
              values.budgetMin,

            max:
              values.budgetMax,

            currency:
              "INR",
          },

          priority:

            values.priority ||
            "medium",

          source:

            values.source ||
            "website",

assignedTo:
    mode === "create"
        ? (
            user?.role === "advisor"
                ? user._id
                : values.assignedTo
          )
        : values.assignedTo,

          specialRequests:
            values.specialRequests,

          notes:
            values.notes,

          tags:
            values.tags || [],

          companyId:

            user?.companyId ||

            user?.company?._id,

          createdBy:
            user?._id,
        };

if (mode === "edit") {
    delete leadData.createdBy;
    delete leadData.companyId;
}

        let response;

        if (
          mode === "edit" &&
          editingLead
        ) {

          response =
            await leadService.update(
              editingLead._id,
              leadData
            );

          message.success(
            "Lead updated successfully!"
          );

        } else {

          response =
            await leadService.create(
              leadData
            );

          message.success(
            "Lead created successfully!"
          );
        }

        if (onSuccess) {

          onSuccess(
            response.data
          );
        }

        form.resetFields();

        onClose();

      } catch (error) {

        console.error(
          "Error saving lead:",
          error
        );


if (error.response?.data?.duplicate) {

  Modal.confirm({

    title: "Duplicate Query Found",

    content: (

      <div>

        <p>

          <strong>Customer:</strong>{" "}

          {error.response.data.lead.customerName}

        </p>

        <p>

          <strong>Phone:</strong>{" "}

          {error.response.data.lead.phone}

        </p>

        <p>

          <strong>Lead ID:</strong>{" "}

          {error.response.data.lead.leadId}

        </p>

        <p>

          This phone number already has a query.

        </p>

      </div>

    ),

    okText: "Open Existing Query",

    cancelText: "Close",

    onOk() {

      onClose();

      navigate(

        `/trips/${error.response.data.lead._id}`

      );

    }

  });

} else {

  message.error(

    error.response?.data?.message ||

    "Failed to save lead"

  );

}

      } finally {

        setLoading(false);
      }
    };

  /* =====================================================
     CANCEL
  ===================================================== */

  const handleCancel =
    () => {

      form.resetFields();

      onClose();
    };

  return (

    <Modal

      title={
        <Space>

          <UserOutlined />

          {

            mode === "edit"

              ? "Edit Lead"

              : "Create New Lead"
          }

        </Space>
      }

      open={visible}

      onCancel={handleCancel}

      width={800}

      footer={[

        <Button
          key="cancel"
          onClick={handleCancel}
        >

          Cancel

        </Button>,

        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={() =>
            form.submit()
          }
        >

          {

            mode === "edit"

              ? "Update Lead"

              : "Create Lead"
          }

        </Button>,
      ]}
    >

      <Form

        form={form}

        layout="vertical"

        onFinish={handleSubmit}

        initialValues={{

          adults: 1,

          children: 0,

          infants: 0,

          priority: "medium",

          source: "website",
        }}
      >

        {/* =====================================================
            CUSTOMER INFO
        ===================================================== */}

        <Divider orientation="left">

          Customer Information

        </Divider>

        <Row gutter={16}>

          <Col span={12}>

            <Form.Item
              name="customerName"
              label="Customer Name"
              rules={[
                {
                  required: true,
                  message:
                    "Please enter customer name",
                },
              ]}
            >

              <Input
                prefix={
                  <UserOutlined />
                }
                placeholder="Enter customer name"
              />

            </Form.Item>

          </Col>

          <Col span={12}>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                {
                  required: true,
                  message:
                    "Please enter email",
                },
                {
                  type: "email",
                  message:
                    "Please enter valid email",
                },
              ]}
            >

              <Input
                prefix={
                  <MailOutlined />
                }
                placeholder="Enter email address"
              />

            </Form.Item>

          </Col>

        </Row>

        <Row gutter={16}>

          <Col span={12}>

            <Form.Item
              name="phone"
              label="Phone Number"
              rules={[
                {
                  required: true,
                  message:
                    "Please enter phone number",
                },
              ]}
            >

              <Input
                prefix={
                  <PhoneOutlined />
                }
                placeholder="Enter phone number"
              />

            </Form.Item>

          </Col>

          <Col span={12}>

            <Form.Item
              name="alternatePhone"
              label="Alternate Phone"
            >

              <Input
                prefix={
                  <PhoneOutlined />
                }
                placeholder="Enter alternate phone"
              />

            </Form.Item>

          </Col>

        </Row>

        {/* =====================================================
            TRAVEL DETAILS
        ===================================================== */}

        <Divider orientation="left">

          Travel Details

        </Divider>

        <Row gutter={16}>

          <Col span={12}>

            <Form.Item
              name="destinations"
              label="Destinations (Multi-Select)"
              rules={[
                {
                  required: true,
                  message:
                    "Please select at least one destination",
                },
              ]}
            >

<Select

  mode="multiple"

  showSearch

  allowClear

  placeholder="Search & select destinations..."

  optionFilterProp="label"

  loading={
    destinationLoading
  }

  options={
    destinationOptions
  }

  /* =====================================
     FORCE OBJECTID VALUE
  ===================================== */

  fieldNames={{
    label: "label",
    value: "value",
  }}

  filterOption={(
    input,
    option
  ) =>

    (
      option?.label || ""
    )
      .toLowerCase()
      .includes(
        input.toLowerCase()
      )
  }

  notFoundContent={

    destinationLoading

      ? <Spin size="small" />

      : "No destinations found"
  }

  suffixIcon={
    <EnvironmentOutlined />
  }

  onChange={(values) => {

    console.log(
      "SELECTED DESTINATIONS:",
      values
    );

    form.setFieldValue(
      "destinations",
      values
    );
  }}
/>

            </Form.Item>

          </Col>

          <Col span={12}>

            <Form.Item
              name="source"
              label="Lead Source"
            >

              <Select placeholder="Select lead source">

                <Option value="website">
                  Website
                </Option>

                <Option value="google_sheets">
                  Google Sheets
                </Option>

                <Option value="csv_import">
                  CSV Import
                </Option>

                <Option value="referral">
                  Referral
                </Option>

                <Option value="social_media">
                  Social Media
                </Option>

                <Option value="advertisement">
                  Advertisement
                </Option>

                <Option value="walk_in">
                  Walk-in
                </Option>

                <Option value="phone_call">
                  Phone Call
                </Option>

              </Select>

            </Form.Item>

          </Col>

        </Row>

        <Row gutter={16}>

          <Col span={12}>

            <Form.Item
              name="travelDate"
              label="Travel Date"
              rules={[
                {
                  required: true,
                  message:
                    "Please select travel date",
                },
              ]}
            >

              <DatePicker
                style={{
                  width: "100%",
                }}
                placeholder="Select travel date"
                format="DD/MM/YYYY"
              />

            </Form.Item>

          </Col>

          <Col span={12}>

            <Form.Item
              name="returnDate"
              label="Return Date"
            >

              <DatePicker
                style={{
                  width: "100%",
                }}
                placeholder="Select return date"
                format="DD/MM/YYYY"
              />

            </Form.Item>

          </Col>

<Form.Item
  label="Duration"
  name="duration"
>
  <InputNumber
    min={1}
    placeholder="Days"
    className="w-full"
  />
</Form.Item>

        </Row>

        {/* =====================================================
            PAX
        ===================================================== */}

        <Row gutter={16}>

          <Col span={8}>

            <Form.Item
              name="adults"
              label="Adults"
            >

              <InputNumber
                min={1}
                max={20}
                style={{
                  width: "100%",
                }}
                placeholder="Adults"
              />

            </Form.Item>

          </Col>

          <Col span={8}>

            <Form.Item
              name="children"
              label="Children"
            >

              <InputNumber
                min={0}
                max={10}
                style={{
                  width: "100%",
                }}
                placeholder="Children"
              />

            </Form.Item>

          </Col>

          <Col span={8}>

            <Form.Item
              name="infants"
              label="Infants"
            >

              <InputNumber
                min={0}
                max={5}
                style={{
                  width: "100%",
                }}
                placeholder="Infants"
              />

            </Form.Item>

          </Col>

        </Row>

        {/* =====================================================
            BUDGET
        ===================================================== */}

        <Divider orientation="left">

          Budget & Priority

        </Divider>

        <Row gutter={16}>

          <Col span={8}>

            <Form.Item
              name="budgetMin"
              label="Minimum Budget (₹)"
            >

              <InputNumber
                style={{
                  width: "100%",
                }}
                placeholder="Min budget"
              />

            </Form.Item>

          </Col>

          <Col span={8}>

            <Form.Item
              name="budgetMax"
              label="Maximum Budget (₹)"
            >

              <InputNumber
                style={{
                  width: "100%",
                }}
                placeholder="Max budget"
              />

            </Form.Item>

          </Col>

          <Col span={8}>

            <Form.Item
              name="priority"
              label="Priority"
            >

              <Select placeholder="Select priority">

                <Option value="low">
                  Low
                </Option>

                <Option value="medium">
                  Medium
                </Option>

                <Option value="high">
                  High
                </Option>

                <Option value="urgent">
                  Urgent
                </Option>

              </Select>

            </Form.Item>

          </Col>

        </Row>

        {/* =====================================================
            ASSIGNMENT
        ===================================================== */}

        <Divider orientation="left">

          Assignment & Tags

        </Divider>

        <Row gutter={16}>

{[
    "admin",
    "superadmin",
    "sales_manager",
    "team_lead"
].includes(user?.role) && (

<Col span={12}>

    <Form.Item
        name="assignedTo"
        label="Assign To"
    >

        <Select
            placeholder="Select team member"
            showSearch
            optionFilterProp="children"
        >

            {users.map((u) => (

                <Option
                    key={u._id}
                    value={u._id}
                >
                    {u.name} ({u.role})
                </Option>

            ))}

        </Select>

    </Form.Item>

</Col>

)}

          <Col span={12}>

            <Form.Item
              name="tags"
              label="Tags"
            >

              <Select
                mode="multiple"
                placeholder="Select tags"
              >

                {tags.map((tag) => (

                  <Option
                    key={tag}
                    value={tag}
                  >

                    {tag}

                  </Option>
                ))}

              </Select>

            </Form.Item>

          </Col>

        </Row>

        {/* =====================================================
            EXTRA
        ===================================================== */}

        <Divider orientation="left">

          Additional Information

        </Divider>

        <Form.Item
          name="specialRequests"
          label="Special Requests"
        >

          <TextArea
            rows={3}
            placeholder="Enter any special requests"
          />

        </Form.Item>

        <Form.Item
          name="notes"
          label="Notes"
        >

          <TextArea
            rows={3}
            placeholder="Enter additional notes"
          />

        </Form.Item>

      </Form>

    </Modal>
  );
};

export default LeadFormModal;
